import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { RepBreadcrumbItems } from 'data/commonData';
import CreateStaffForm from 'components/forms/CreateStaffForm';
import {
  CreateRepDTO,
  CreateRepFormData
} from 'data/from-data/create-customer-rep-form.data';
import { CREATE_CUSTOMER_REP } from 'services/api/customer-rep';
import { toast } from 'react-hot-toast';
import { formatPhoneNumber } from 'pages/apps/employee/utils/helper';

const CreateRep: React.FC = () => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createCustomerRepDto, setCreateCustomerRepDto] =
    useState<CreateRepFormData>(new CreateRepFormData());
  const [isLoading, setLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const navigate = useNavigate();
  const queries = useQueryClient();

  const createRepAction = useMutation(
    'createRepresentative',
    CREATE_CUSTOMER_REP,
    {
      onError: (err: any) => {
        console.log(err);
        setLoading(false);
        if (err.response?.data) {
          const errorData = err.response.data;
          if (errorData.messages && Array.isArray(errorData.messages)) {
            errorData.messages.forEach((message: string) => {
              toast.error(message);
            });
          } else {
            toast.error('An error occurred while creating the representative');
          }
        }
      },
      onSuccess: () => {
        setLoading(false);
        queries.invalidateQueries('get_customer_reps');
        navigate('/home/apps/representative');
        toast.success('Customer Representative created Successfully');
      },
      onMutate: () => {
        setLoading(true);
      }
    }
  );

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    const { firstName, lastName, mobile, email, client } = createCustomerRepDto;

    if (!firstName.value) errors.firstName = 'First name is required';
    if (!lastName.value) errors.lastName = 'Last name is required';
    if (!client.value) errors.client = 'Customer is required';
    if (!email.value) errors.email = 'Email is required';

    if (!mobile.value) {
      errors.mobile = 'Mobile is required';
    }

    if (email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
      errors.email = 'Please enter a valid email address';
    }

    if (email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
      errors.email = 'Please enter a valid email address';
    }

    if (mobile.value && mobile.value.replace(/\D/g, '').length < 10) {
      errors.mobile = 'Please enter a valid mobile number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (validateForm()) {
      runFormSubmissionAction(event);
    }

    setValidated(true);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'client') {
      setCreateCustomerRepDto(prevData => ({
        ...prevData,
        [name]: {
          ...prevData[name],
          value: { id: value }
        }
      }));
    } else {
      setCreateCustomerRepDto(prevData => ({
        ...prevData,
        [name]: {
          ...prevData[name],
          value
        }
      }));
    }

    setFormErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      firstName: { value: firstName },
      lastName: { value: lastName },
      mobile: { value: mobile },
      phone: { value: phone },
      title: { value: title },
      email: { value: email },
      client: { value: client },
      notes: { value: notes }
    } = createCustomerRepDto;

    const payload: CreateRepDTO = {
      firstName,
      lastName,
      mobile: mobile ? formatPhoneNumber(mobile) : '',
      phone: phone ? formatPhoneNumber(phone) : '',
      title,
      client,
      notes,
      ...(email && { email })
    };

    await createRepAction.mutateAsync(payload);
  };

  const renderFormFields = () => {
    return Object.entries(createCustomerRepDto).map(([key, formProps]) => (
      <Col md="6" key={key}>
        <CreateStaffForm
          formProps={{
            ...formProps,
            required:
              ['firstName', 'lastName', 'client'].includes(key) ||
              (key === 'email' && !createCustomerRepDto.mobile.value) ||
              (key === 'mobile' && !createCustomerRepDto.email.value)
          }}
          handleInputChange={handleInputChange}
          error={formErrors[key]}
        />
      </Col>
    ));
  };

  return (
    <div className="mb-9">
      <PageBreadcrumb items={RepBreadcrumbItems} />
      <div className="border-bottom mb-7 mx-n3 px-2 mx-lg-n6 px-lg-6">
        <Row>
          <Col xl={9}>
            <div className="d-sm-flex justify-content-between">
              <h2 className="mb-4">Create Representative</h2>
              <div className="d-flex mb-3">
                <Link to="/home/apps/representative">
                  <Button variant="phoenix-primary" className="me-2 px-6">
                    Cancel
                  </Button>
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <Row>
        <Col xl={9}>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <h4 className="mb-3">Representative Information</h4>
            <Row className="g-3 mb-8">{renderFormFields()}</Row>
            <div className="text-end">
              <Button
                variant="primary"
                className="mt-6"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Representative'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default CreateRep;
